import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { toast } from 'react-toastify';
import { ChevronLeft, ChevronRight, Download, Check, CheckCheck, Undo2, X, Wallet, ArrowLeft, History, FileSpreadsheet, Pencil, ExternalLink, CalendarCheck, CalendarRange, Clock } from 'lucide-react';
import { windowFor, shiftWindow, plusDays, todayCT, payWindowModeOf, windowNoun, rangeFor, rangeProblem, shiftRange, quickRanges, PayWindowMode, PayViewMode } from '../utils/payWindow';
import { useReadOnly } from '../hooks/useReadOnly';

/**
 * Crew Pay — WHO to pay, HOW MUCH, WHEN, HOW. Michael pays outside the app
 * (Venmo / Zelle / cash / check); this page is the ledger. Nothing here moves
 * money. The ledger is weekly (Mon–Sun Central, due the following Monday);
 * the page groups those weeks into the pay window Michael actually pays —
 * two weeks (Week B + Week A) or, since Oct 27 2026, a calendar month
 * ("1st – 1st") when Settings → Crew & pay → Crew pay window says Monthly.
 * A ledger week that straddles the 1st counts in the month its Monday falls in
 * (utils/payWindow.ts, same rule as the server). A 1-week view is a toggle away,
 * and "Any dates" (Nov 1 2026: "find pay through any dates") shows a range he types —
 * every ledger week whose Monday falls in it, the same rule, kept in the URL as
 * ?start=&end= so a link brings it back.
 *
 * Landing = the person list only ("James $120"). Tap a person for every job,
 * the hours (measured clock, else the estimate marked as such), what was
 * charged, and their pay; mark sent / confirmed; audit log. The math is
 * unchanged: the estimate is the pay (or a pinned flat amount), never minutes.
 */

type Entry = {
  id: number; job_id: number | null; employee_id: number; employee_name: string;
  customer_name: string | null; address: string | null; job_title: string | null;
  route_name: string | null; scheduled_date: string | null; completed_at: string | null;
  amount: number | string; amount_source: string | null; is_partial: number; partial_note: string | null;
  attribution: string | null; flags: string | null;
  status: 'owed' | 'sent' | 'confirmed' | 'void'; method: string | null; method_ref: string | null; note: string | null;
  sent_at: string | null; confirmed_at: string | null; due_date: string;
  pay_period_start?: string; pay_period_end?: string;   // the entry's own ledger week (Mon–Sun)
  // from the job, for display only (same rule as the hours/pay sheet)
  hours_minutes?: number; hours_source?: string; estimated_minutes?: number | null; charged?: number | null; job_status?: string;
  // a tip share: which visits it was for, and (admin) what to know when some of it went to someone not on the visit
  tip_for_text?: string | null; tip_check?: string[] | null;
};

/** Hours for one pay row: the clock when there is one, otherwise the estimate, marked. */
function hoursOf(e: Entry): { text: string; measured: boolean; hours: number } {
  const m = Number(e.hours_minutes || 0);
  if (m > 0) return { text: `${(m / 60).toFixed(2)} h`, measured: true, hours: m / 60 };
  const est = Number(e.estimated_minutes || 0);
  if (est > 0) return { text: `~${(est / 60).toFixed(2)} h est.`, measured: false, hours: est / 60 };
  return { text: 'no time', measured: false, hours: 0 };
}

const METHOD_LABEL: Record<string, string> = { venmo: 'Venmo', zelle: 'Zelle', cash: 'Cash', check: 'Check', other: 'Other' };
const SOURCE_LABEL: Record<string, string> = { flat: 'flat (per person)', estimate: 'estimate', zero: 'not set', partial_cut: 'partial cut', tip: 'tip — their share' };

function fmtMonth(iso: string) {
  return new Date(iso.slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}
function fmtDay(iso?: string | null) {
  if (!iso) return '—';
  return new Date(String(iso).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}
function fmtTs(ts?: string | null, withTime = false) {
  if (!ts) return '—';
  let s = String(ts).replace(' ', 'T');
  if (/[+-]\d{2}$/.test(s)) s += ':00'; else if (!/Z$|[+-]\d{2}:?\d{2}$/.test(s)) s += 'Z';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/Chicago', ...(withTime ? { hour: 'numeric', minute: '2-digit' } : {}) });
}
const money = (n: number | string) => `$${Number(n || 0).toFixed(2)}`;

const isMobile = () => /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
/** Venmo deep link — opens the app (or venmo.com) with the amount and note filled in. Michael still taps Pay inside Venmo. */
function venmoLink(handle: string, amount: number, note: string) {
  const h = encodeURIComponent(handle.replace(/^@/, ''));
  const a = amount.toFixed(2);
  const n = encodeURIComponent(note);
  return isMobile()
    ? `venmo://paycharge?txn=pay&recipients=${h}&amount=${a}&note=${n}`
    : `https://venmo.com/${h}?txn=pay&amount=${a}&note=${n}`;
}

function downloadBlob(data: any, type: string, name: string) {
  const url = URL.createObjectURL(new Blob([data], { type }));
  const a = document.createElement('a');
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

export default function Pay() {
  const readOnly = useReadOnly();   // view-only login: grey out what can only ever be refused
  const qc = useQueryClient();
  const [anchor, setAnchor] = useState<string>('');          // '' = today
  const [view, setView] = useState<'window' | 'week'>('window');   // the pay window (two weeks or the month) by default
  // "Any dates": the range lives in the URL (?start=&end=) so a link reproduces it.
  const [searchParams, setSearchParams] = useSearchParams();
  const range = useMemo(() => rangeFor(searchParams.get('start'), searchParams.get('end')), [searchParams]);
  const [rangeOpen, setRangeOpen] = useState(false);
  const [draftStart, setDraftStart] = useState('');
  const [draftEnd, setDraftEnd] = useState('');
  const [person, setPerson] = useState<number | null>(null);
  const [status, setStatus] = useState<'all' | 'owed' | 'sent' | 'confirmed'>('all');
  const [sentSheet, setSentSheet] = useState<Entry | null>(null);
  const [sheetMethod, setSheetMethod] = useState('venmo');
  const [sheetAmount, setSheetAmount] = useState('');
  const [sheetNote, setSheetNote] = useState('');
  const [bulkMethod, setBulkMethod] = useState('venmo');
  const [openHistory, setOpenHistory] = useState<number | null>(null);
  const [hoursBusy, setHoursBusy] = useState(false);
  const [paidThroughOpen, setPaidThroughOpen] = useState(false);
  const [paidThroughDate, setPaidThroughDate] = useState('');
  const [paidThroughMethod, setPaidThroughMethod] = useState('venmo');

  // How he pays: every two weeks (the default) or monthly, 1st to 1st — Settings → Crew & pay.
  const { data: windowInfo, isLoading: windowLoading } = useQuery('pay-window',
    () => api.get('/crew-pay/window').then(r => r.data as { mode: PayWindowMode }), { staleTime: 60_000 });
  const mode: PayWindowMode = payWindowModeOf(windowInfo?.mode);
  const viewMode: PayViewMode = view === 'week' ? 'week' : mode;

  // The visible span: the pay window (its ledger weeks, win.weeks), one week, or the typed range.
  const base = anchor || todayCT();
  const win = useMemo(() => windowFor(base, viewMode), [base, viewMode]);
  const weeks = range ? range.weeks : win.weeks;
  // A range has no single due day: its last week's Monday-after stands in where one is needed.
  const period = range
    ? { start: range.start, end: range.end, due: weeks.length ? plusDays(weeks[weeks.length - 1], 7) : range.end }
    : { start: win.start, end: win.end, due: win.due };
  // The dates on screen, for the driving between stops.
  const travelFrom = weeks[0] || period.start;
  const travelTo = weeks.length ? plusDays(weeks[weeks.length - 1], 6) : period.end;
  const isCurrent = !range && win.start === windowFor(todayCT(), viewMode).start;
  const spanNoun = range ? 'range' : windowNoun(viewMode);
  const openRange = (start: string, end: string) => { setSearchParams({ start, end }); setRangeOpen(false); setPerson(null); };
  const clearRange = () => { setSearchParams({}); setRangeOpen(false); };
  // Prev / next: the window before or after, or the range stepped by its own length.
  const go = (n: number) => {
    if (range) { const r = shiftRange(range, n); if (r) openRange(r.start, r.end); return; }
    setAnchor(shiftWindow(win, n).start);
  };
  const draftProblem = rangeProblem(draftStart, draftEnd);

  const { data, isLoading } = useQuery(
    ['crew-pay', range ? `${range.start}..${range.end}` : win.start, range ? 'range' : viewMode],
    // One call for every ledger week in the window (GET /crew-pay?window=) or the range (?start=&end=). Amounts are exactly the server's.
    () => api.get('/crew-pay', { params: range ? { start: range.start, end: range.end } : { period_start: win.start, window: viewMode } }).then(r => r.data),
    { keepPreviousData: true, enabled: !windowLoading }
  );

  const entries: Entry[] = useMemo(() => data?.entries || [], [data]);
  const lastMethods: Record<string, string> = data?.last_methods || {};
  const methods: string[] = data?.methods || ['venmo', 'zelle', 'cash', 'check', 'other'];

  const refresh = () => qc.invalidateQueries('crew-pay');

  const markSent = useMutation(
    (v: { id: number; method: string; amount?: string; note?: string }) =>
      api.post(`/crew-pay/${v.id}/sent`, { method: v.method, amount: v.amount || undefined, note: v.note || undefined }),
    { onSuccess: () => { toast.success('Marked sent'); setSentSheet(null); refresh(); },
      onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed'); } }
  );
  const confirm = useMutation(
    (v: { id: number; method?: string }) => api.post(`/crew-pay/${v.id}/confirm`, { method: v.method }),
    { onSuccess: () => { toast.success('Confirmed'); refresh(); },
      onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed'); } }
  );
  const undo = useMutation(
    (id: number) => api.post(`/crew-pay/${id}/undo`),
    { onSuccess: () => { toast.info('Back to owed'); refresh(); },
      onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed'); } }
  );
  const bulkSent = useMutation(
    (v: { ids: number[]; method: string }) => api.post('/crew-pay/bulk-sent', { entry_ids: v.ids, method: v.method }),
    { onSuccess: (r: any) => { toast.success(r.data?.message || 'Done'); refresh(); },
      onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed'); } }
  );
  // "Pay $30 of what they're owed" — oldest stops first; the last one is split so the rest stays owed.
  const [partAmount, setPartAmount] = useState('');
  const payAmount = useMutation(
    (v: { employee_id: number; amount: number; method: string }) => api.post('/crew-pay/pay-amount', v),
    { onSuccess: (r: any) => { toast.success(r.data?.message || 'Done'); refresh(); },
      onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed'); } }
  );
  // "Everyone is paid up through Sunday" — one tap, every owed entry through that date.
  const markPaidThrough = useMutation(
    (v: { through_date: string; method: string }) => api.post('/crew-pay/mark-paid-through', v),
    { onSuccess: (r: any) => { toast.success(r.data?.message || 'Done'); setPaidThroughOpen(false); refresh(); },
      onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed'); } }
  );

  // People with subtotals — the landing list
  const people = useMemo(() => {
    const m = new Map<number, { id: number; name: string; rows: Entry[]; owed: number; sent: number; confirmed: number; total: number; hours: number; estRows: number; tipChecks: number }>();
    for (const e of entries) {
      if (!m.has(e.employee_id)) m.set(e.employee_id, { id: e.employee_id, name: e.employee_name, rows: [], owed: 0, sent: 0, confirmed: 0, total: 0, hours: 0, estRows: 0, tipChecks: 0 });
      const g = m.get(e.employee_id)!;
      g.rows.push(e);
      if (e.tip_check?.length) g.tipChecks += 1;
      if (e.status === 'owed' || e.status === 'sent' || e.status === 'confirmed') { g[e.status] += Number(e.amount || 0); g.total += Number(e.amount || 0); }
      const h = hoursOf(e);
      if (h.measured) g.hours += h.hours; else if (h.hours > 0) g.estRows += 1;
    }
    return Array.from(m.values()).sort((a, b) => b.total - a.total || a.name.localeCompare(b.name));
  }, [entries]);
  const periodHours = useMemo(() => people.reduce((s, p) => s + p.hours, 0), [people]);
  const periodTotal = useMemo(() => people.reduce((s, p) => s + p.total, 0), [people]);

  const selected = person != null ? people.find(p => p.id === person) || null : null;
  const selectedRows = useMemo(
    () => (selected ? selected.rows.filter(e => status === 'all' || e.status === status) : []),
    [selected, status]
  );

  function openSent(e: Entry) {
    setSentSheet(e);
    setSheetMethod(lastMethods[e.employee_id] || data?.default_method || 'venmo');
    setSheetAmount(String(Number(e.amount || 0).toFixed(2)));
    setSheetNote('');
  }

  function exportCsv() {
    const header = ['Person', 'Date', 'Customer', 'Address', 'Job', 'Route', 'Hours', 'Hours source', 'Charged', 'Completed', 'Pay period', 'Due', 'Amount', 'Source', 'Partial', 'Method', 'Status', 'Sent', 'Confirmed', 'Flags'];
    const esc = (v: any) => { const s = v == null ? '' : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const rows = entries.map(e => [
      e.employee_name, e.scheduled_date || '', e.customer_name, e.address, e.job_title || e.tip_for_text || '', e.route_name || '',
      hoursOf(e).hours.toFixed(2), hoursOf(e).measured ? 'clock' : (hoursOf(e).hours > 0 ? 'estimate' : 'none'), Number(e.charged || 0).toFixed(2),
      fmtTs(e.completed_at),
      range && e.pay_period_start ? `${e.pay_period_start} – ${e.pay_period_end || plusDays(e.pay_period_start, 6)}` : `${period.start} – ${period.end}`,
      range ? (e.due_date || '') : period.due,
      Number(e.amount || 0).toFixed(2), SOURCE_LABEL[e.amount_source || ''] || e.amount_source || '',
      e.is_partial ? 'yes' : '', e.method ? METHOD_LABEL[e.method] : '', e.status,
      fmtTs(e.sent_at), fmtTs(e.confirmed_at), e.flags || '',
    ].map(esc).join(','));
    const totals = data?.totals || { owed: 0, sent: 0, confirmed: 0 };
    rows.push('');
    const pad = (label: string, v: number) => [label, '', '', '', '', '', '', '', '', '', '', '', v.toFixed(2)].join(',');
    rows.push(pad('TOTAL PAY', periodTotal));
    rows.push(pad('TOTAL OWED', Number(totals.owed || 0)));
    rows.push(pad('TOTAL SENT', Number(totals.sent || 0)));
    rows.push(pad('TOTAL CONFIRMED', Number(totals.confirmed || 0)));
    rows.push(['TOTAL HOURS (clocked)', '', '', '', '', '', periodHours.toFixed(2)].join(','));
    downloadBlob(header.join(',') + '\n' + rows.join('\n'), 'text/csv', `crew-pay-${period.start}_to_${period.end}.csv`);
  }

  // The owner's hours/pay sheet for this period: measured clocks only, pay from this ledger.
  async function exportHoursPay() {
    setHoursBusy(true);
    try {
      const res = await api.get('/export/hours-pay.csv', { params: { start: period.start, end: period.end }, responseType: 'blob' });
      downloadBlob(res.data, 'text/csv;charset=utf-8', `hours-pay-${period.start}_to_${period.end}.csv`);
    } catch {
      toast.error('Could not build the hours/pay sheet');
    } finally { setHoursBusy(false); }
  }

  const owedIdsFor = (rows: Entry[]) => rows.filter(e => e.status === 'owed').map(e => e.id);

  // Month / 2 wks / 1 wk / Any dates. Picking a window view clears a range; "Any dates" opens the control.
  const pickView = (v: 'window' | 'week') => { setView(v); if (range) clearRange(); };
  const viewBtn = (on: boolean) => `press px-2 py-1 rounded-xl font-medium ${on ? 'bg-brand-600 text-black' : 'text-gray-500 hover:bg-gray-100'}`;
  const periodCard = (
    <div className="card p-3 sm:p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
        <button onClick={() => go(-1)} className="p-2 rounded-lg hover:bg-gray-100" title={`Previous ${spanNoun}`}><ChevronLeft className="h-5 w-5" /></button>
        <div className="flex-1 min-w-[10rem]">
          <p className="text-sm font-bold text-gray-900">
            {range ? range.words
              : viewMode === 'monthly' ? fmtMonth(period.start)
              : `${viewMode === 'week' ? 'Week of' : 'Pay window'} ${fmtDay(period.start)} – ${fmtDay(period.end)}`}
            {isCurrent && <span className="ml-2 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-brand-100 text-brand-700">current</span>}
          </p>
          <p className="text-xs text-gray-500">
            {range
              ? `Any dates · every pay week whose Monday falls in these dates (${range.days} day${range.days === 1 ? '' : 's'}) · arrows step by the same length`
              : viewMode === 'monthly'
                ? `1st to 1st · ${win.weeks.length} pay weeks (a week counts in the month its Monday falls in) · due ${fmtDay(period.due)}`
                : `${viewMode === 'biweekly' ? 'Two pay weeks (Week B + Week A) · ' : ''}ends Sunday (Central) · due ${fmtDay(period.due)}`}
          </p>
        </div>
        <button onClick={() => go(1)} className="p-2 rounded-lg hover:bg-gray-100" title={`Next ${spanNoun}`}><ChevronRight className="h-5 w-5" /></button>
        <div className="flex items-center gap-1 text-xs">
          <button onClick={() => pickView('window')} className={viewBtn(!range && view === 'window')}>{mode === 'monthly' ? 'Month' : '2 wks'}</button>
          <button onClick={() => pickView('week')} className={viewBtn(!range && view === 'week')}>1 wk</button>
          <button onClick={() => { setDraftStart(range?.start || period.start); setDraftEnd(range?.end || period.end); setRangeOpen(o => !o); }}
            className={`${viewBtn(!!range)} flex items-center gap-1`} title="Pay between any two dates">
            <CalendarRange className="h-3.5 w-3.5" /> Any dates
          </button>
        </div>
        {!isCurrent && !range && (
          <button onClick={() => setAnchor('')} className="text-xs text-brand-600 font-medium">This {windowNoun(viewMode)}</button>
        )}
        {range && !rangeOpen && (
          <button onClick={clearRange} className="text-xs text-brand-600 font-medium flex items-center gap-1"><X className="h-3 w-3" /> Back to {windowNoun(mode)}s</button>
        )}
      </div>
      {rangeOpen && (
        <div className="pt-3 border-t border-gray-100 space-y-2">
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <label className="label-sm">From</label>
              <input type="date" value={draftStart} onChange={e => setDraftStart(e.target.value)} className="input text-sm py-1" />
            </div>
            <div>
              <label className="label-sm">To</label>
              <input type="date" value={draftEnd} onChange={e => setDraftEnd(e.target.value)} className="input text-sm py-1" />
            </div>
            <button onClick={() => { if (!draftProblem) openRange(draftStart, draftEnd); }} disabled={!!draftProblem}
              className="btn-primary text-sm py-1.5 disabled:opacity-50" title={draftProblem || 'Show pay between these dates'}>Apply</button>
            {range && <button onClick={clearRange} className="btn-secondary text-sm py-1.5">Clear</button>}
            <button onClick={() => setRangeOpen(false)} className="p-1.5 rounded-lg text-gray-500 hover:text-gray-700" title="Close"><X className="h-4 w-4" /></button>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 text-xs">
            {quickRanges().map(q => (
              <button key={q.label} onClick={() => openRange(q.start, q.end)}
                className={`press px-2 py-1 rounded-full border font-medium ${range && range.start === q.start && range.end === q.end ? 'bg-brand-600 text-black border-brand-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>{q.label}</button>
            ))}
            <span className="text-gray-500 ml-1">{draftProblem && draftStart && draftEnd ? draftProblem : 'Both dates included · a pay week counts where its Monday falls · up to two years'}</span>
          </div>
        </div>
      )}
    </div>
  );

  // ── Person detail ──────────────────────────────────────────────────────────
  if (selected) {
    const owedIds = owedIdsFor(selected.rows);
    return (
      <div className="space-y-4">
        <button onClick={() => { setPerson(null); setStatus('all'); setOpenHistory(null); }} className="text-sm text-gray-500 hover:text-gray-800 flex items-center gap-1">
          <ArrowLeft className="h-4 w-4" /> All people
        </button>
        {periodCard}

        <div className="card p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-2xl font-black text-gray-900">{selected.name}</h1>
              <p className="text-sm text-gray-500 mt-0.5">
                {selected.rows.length} job{selected.rows.length === 1 ? '' : 's'} · {selected.hours.toFixed(2)} h clocked{selected.estRows ? ` (+${selected.estRows} on estimate)` : ''} · <b className="text-gray-800">{money(selected.total)}</b> total
                {lastMethods[selected.id] ? ` · usually ${METHOD_LABEL[lastMethods[selected.id]]}` : ''}
              </p>
            </div>
            <div className="flex gap-2">
              {(['owed', 'sent', 'confirmed'] as const).map(k => {
                const v = selected[k];
                return (
                  <button key={k} onClick={() => setStatus(status === k ? 'all' : k)}
                    className={`rounded-xl px-3 py-2 text-left border-2 ${status === k ? 'border-brand-500' : 'border-transparent'} ${k === 'owed' ? 'bg-amber-50' : k === 'sent' ? 'bg-blue-50' : 'bg-green-50'}`}>
                    <p className={`text-[10px] font-bold uppercase tracking-wide ${k === 'owed' ? 'text-amber-700' : k === 'sent' ? 'text-blue-700' : 'text-green-700'}`}>{k}</p>
                    <p className="text-lg font-black text-gray-900 leading-tight">{money(v)}</p>
                  </button>
                );
              })}
            </div>
          </div>
          {owedIds.length > 0 && (() => {
            const handle = (data?.people || []).find((p: any) => p.id === selected.id)?.venmo_handle as string | undefined;
            const first = selected.name.split(' ')[0];
            return (
              <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap items-center gap-2">
                {handle ? (
                  <a href={venmoLink(handle, selected.owed, `Keep Austin Mowed — ${fmtDay(period.start)} to ${fmtDay(period.end)}`)}
                    target={isMobile() ? undefined : '_blank'} rel="noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-snow text-xs font-bold" style={{ backgroundColor: '#008CFF' }}
                    title={`Opens Venmo to @${handle} with ${money(selected.owed)} filled in — you still confirm in Venmo`}>
                    <ExternalLink className="h-3.5 w-3.5" /> Pay {money(selected.owed)} in Venmo
                  </a>
                ) : (
                  <Link to="/employees" className="text-xs text-gray-500 underline">Add {first}'s Venmo handle in Employees for one-tap pay</Link>
                )}
                <span className="text-xs text-gray-500">then</span>
              </div>
            );
          })()}
          {owedIds.length > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className="text-sm text-gray-700">Record {money(selected.owed)} as sent via</span>
              <select value={bulkMethod} onChange={e => setBulkMethod(e.target.value)} className="input text-xs w-auto py-1">
                {methods.map(m => <option key={m} value={m}>{METHOD_LABEL[m]}</option>)}
              </select>
              <button
                onClick={() => { if (window.confirm(`Mark all ${owedIds.length} owed entries (${money(selected.owed)}) for ${selected.name} as sent via ${METHOD_LABEL[bulkMethod]}?`)) bulkSent.mutate({ ids: owedIds, method: bulkMethod }); }}
                disabled={readOnly || (bulkSent.isLoading)}
                className="btn-primary text-xs py-1.5 flex items-center gap-1 disabled:opacity-50">
                <Check className="h-3.5 w-3.5" /> Mark all owed as sent
              </button>
              {status !== 'all' && <button onClick={() => setStatus('all')} className="text-xs text-gray-500 flex items-center gap-1 ml-auto"><X className="h-3 w-3" /> clear filter</button>}
            </div>
          )}
          {owedIds.length > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className="text-sm text-gray-700">Or pay part of it: $</span>
              <input type="number" min="0.01" step="0.01" inputMode="decimal" value={partAmount} onChange={e => setPartAmount(e.target.value)} placeholder="30" className="input text-sm w-24 py-1" />
              <span className="text-xs text-gray-500">via {METHOD_LABEL[bulkMethod]}</span>
              <button
                onClick={() => { const amt = Number(partAmount); if (!(amt > 0)) return; if (window.confirm(`Record $${amt.toFixed(2)} paid to ${selected.name} via ${METHOD_LABEL[bulkMethod]}? It goes to the oldest stops first; the rest stays owed.`)) payAmount.mutate({ employee_id: selected.id, amount: amt, method: bulkMethod }, { onSuccess: () => setPartAmount('') }); }}
                disabled={readOnly || (payAmount.isLoading || !(Number(partAmount) > 0))}
                className="btn-secondary text-xs py-1.5 flex items-center gap-1 disabled:opacity-50">
                <Check className="h-3.5 w-3.5" /> Record part payment
              </button>
            </div>
          )}
        </div>

        <div className="card overflow-hidden">
          {selectedRows.length === 0 ? (
            <div className="p-8 text-center text-gray-500 text-sm">Nothing {status !== 'all' ? status : ''} {range ? 'in these dates' : 'this period'}.</div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {selectedRows.map(e => (
                <li key={e.id} className="px-4 py-3">
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                    <div className="flex-1 min-w-[12rem]">
                      <p className="text-sm font-medium text-gray-900">
                        {e.job_id ? <Link to={`/jobs/${e.job_id}`} className="hover:text-brand-600">{e.customer_name || 'Job'}</Link> : (e.customer_name || (e.amount_source === 'inspection_bonus' ? '⭐ Quality bonus' : 'Project'))}
                        {!e.job_id && e.amount_source === 'inspection_bonus' && e.note ? <span className="text-gray-500 font-normal"> — {String(e.note).replace(/^Quality bonus — /, '')}</span> : ''}
                        {e.amount_source === 'tip' && e.tip_for_text ? <span className="text-gray-500 font-normal"> · {e.tip_for_text}</span> : ''}
                        {e.job_title ? <span className="text-gray-500 font-normal"> — {e.job_title}</span> : ''}
                      </p>
                      <p className="text-xs text-gray-500">
                        <span className="font-medium text-gray-700">{fmtDay(e.scheduled_date)}</span>
                        {e.address ? ` · ${e.address.split(',')[0]}` : ''}
                        {e.route_name ? ` · ${e.route_name}` : ''}
                        {e.completed_at ? ` · done ${fmtTs(e.completed_at)}` : ''}
                      </p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {(() => { const h = hoursOf(e); return (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded inline-flex items-center gap-0.5 ${h.measured ? 'bg-sky-50 text-sky-800 border border-sky-200' : 'bg-gray-100 text-gray-500'}`} title={h.measured ? 'Measured start → finish' : 'No clock on this job — estimate shown'}>
                            <Clock className="h-2.5 w-2.5" /> {h.text}
                          </span>
                        ); })()}
                        {Number(e.charged) > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">charged {money(Number(e.charged))}</span>}
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{SOURCE_LABEL[e.amount_source || ''] || e.amount_source}</span>
                        {!!e.is_partial && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800" title={e.partial_note || ''}>partial — full pay</span>}
                        {e.attribution === 'completer' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-200">unassigned — attributed to completer</span>}
                        {(e.flags || '').includes('pay not set') && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700">pay not set</span>}
                        {(e.flags || '').includes('per person') && <span className="text-[10px] px-1.5 py-0.5 rounded bg-brand-50 text-brand-700" title="A flat amount on the job pays each person that amount">per person</span>}
                      </div>
                      {!!e.tip_check?.length && (
                        <div className="mt-1.5 rounded-lg bg-amber-50 border border-amber-200 px-2.5 py-1.5 text-xs text-amber-900 space-y-0.5">
                          {e.tip_check.map((line, i) => <p key={i}>{line}</p>)}
                        </div>
                      )}
                    </div>
                    <p className="text-base font-bold text-gray-900 w-20 text-right">{money(e.amount)}</p>
                    <div className="w-28 text-xs">
                      <span className={`px-2 py-0.5 rounded-full font-semibold ${e.status === 'owed' ? 'bg-amber-100 text-amber-800' : e.status === 'sent' ? 'bg-blue-100 text-blue-800' : e.status === 'confirmed' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>{e.status}</span>
                      {e.method && <p className="text-gray-500 mt-0.5">{METHOD_LABEL[e.method]}{e.method_ref ? ` · ${e.method_ref}` : ''}{e.sent_at ? ` · ${fmtTs(e.sent_at)}` : ''}</p>}
                    </div>
                    <div className="flex items-center gap-1">
                      {e.status === 'owed' && (
                        <button onClick={() => openSent(e)} className="press text-xs px-2.5 py-1.5 rounded-xl bg-brand-600 text-black font-semibold flex items-center gap-1"><Check className="h-3.5 w-3.5" /> Sent</button>
                      )}
                      {(e.status === 'owed' || e.status === 'sent') && (
                        <button disabled={readOnly} onClick={() => confirm.mutate({ id: e.id, method: e.method || lastMethods[e.employee_id] || 'venmo' })}
                          className="text-xs px-2.5 py-1.5 rounded-lg border border-green-300 text-green-700 font-semibold flex items-center gap-1" title={e.status === 'owed' ? 'Records sent + confirmed' : 'Confirm received'}>
                          <CheckCheck className="h-3.5 w-3.5" /> Confirm
                        </button>
                      )}
                      {(e.status === 'sent' || e.status === 'confirmed') && (
                        <button disabled={readOnly} onClick={() => undo.mutate(e.id)} className="p-1.5 rounded-lg text-gray-500 hover:text-amber-600" title="Back to owed"><Undo2 className="h-4 w-4" /></button>
                      )}
                      <button onClick={() => setOpenHistory(openHistory === e.id ? null : e.id)} className={`p-1.5 rounded-lg ${openHistory === e.id ? 'text-brand-600 bg-brand-50' : 'text-gray-500 hover:text-gray-700'}`} title="Audit log"><History className="h-4 w-4" /></button>
                      {e.job_id && (
                        (e.flags || '').includes('pay not set')
                          ? <Link to={`/jobs/${e.job_id}?edit=pay`} className="text-xs px-2.5 py-1.5 rounded-lg bg-red-600 text-white font-semibold flex items-center gap-1"><Pencil className="h-3.5 w-3.5" /> Set pay</Link>
                          : <Link to={`/jobs/${e.job_id}?edit=pay`} className="p-1.5 rounded-lg text-gray-500 hover:text-brand-600" title="Adjust pay on the job"><Pencil className="h-4 w-4" /></Link>
                      )}
                    </div>
                  </div>
                  {openHistory === e.id && <EntryHistory entryId={e.id} />}
                </li>
              ))}
            </ul>
          )}
        </div>

        {sentSheet && <SentSheet entry={sentSheet} methods={methods} lastMethod={lastMethods[sentSheet.employee_id]}
          method={sheetMethod} setMethod={setSheetMethod} amount={sheetAmount} setAmount={setSheetAmount} note={sheetNote} setNote={setSheetNote}
          busy={markSent.isLoading} onClose={() => setSentSheet(null)}
          onSave={() => markSent.mutate({ id: sentSheet.id, method: sheetMethod, amount: sheetAmount, note: sheetNote })} />}
      </div>
    );
  }

  // ── Landing: people only ───────────────────────────────────────────────────
  const totals = data?.totals || { owed: 0, sent: 0, confirmed: 0 };
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-gray-900 flex items-center gap-2"><Wallet className="h-6 w-6 text-brand-600" /> Crew Pay</h1>
          <p className="text-sm text-gray-500 mt-0.5">Who worked and what you owe them for {range ? 'these dates' : `this ${windowNoun(viewMode)}`}. Tap a name for every job, the hours and their pay. Paid outside the app — this is the record.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => { setPaidThroughDate(prev => prev || plusDays(period.start, -1)); setPaidThroughOpen(true); }} disabled={!data}
            className="btn-secondary text-sm flex items-center gap-1.5 disabled:opacity-40" title="Mark every owed entry through a date as paid">
            <CalendarCheck className="h-4 w-4" /> Mark paid through…
          </button>
          <button onClick={exportHoursPay} disabled={!period || hoursBusy} className="btn-secondary text-sm flex items-center gap-1.5 disabled:opacity-40" title={`Measured hours + pay for ${range ? 'these dates' : 'this period'} (spreadsheet columns)`}>
            <FileSpreadsheet className="h-4 w-4" /> {hoursBusy ? 'Building…' : 'Hours/pay sheet'}
          </button>
          <button onClick={exportCsv} disabled={!entries.length} className="btn-secondary text-sm flex items-center gap-1.5 disabled:opacity-40">
            <Download className="h-4 w-4" /> Pay CSV
          </button>
        </div>
      </div>

      {periodCard}

      {paidThroughOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" onClick={() => setPaidThroughOpen(false)}>
          <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-gray-900">Everyone is paid up through…</h3>
              <button onClick={() => setPaidThroughOpen(false)}><X className="h-5 w-5 text-gray-500" /></button>
            </div>
            <p className="text-sm text-gray-600">Every owed entry whose pay week ended on or before this date is recorded as sent and confirmed. Each person's usual method is used; pick a fallback for anyone paid for the first time. Entries with no pay set stay owed so you can fix them first.</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-sm">Through (a Sunday)</label>
                <input type="date" value={paidThroughDate} onChange={e => setPaidThroughDate(e.target.value)} className="input" />
              </div>
              <div>
                <label className="label-sm">Fallback method</label>
                <select value={paidThroughMethod} onChange={e => setPaidThroughMethod(e.target.value)} className="input">
                  {methods.map(m => <option key={m} value={m}>{METHOD_LABEL[m]}</option>)}
                </select>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setPaidThroughOpen(false)} className="btn-secondary flex-1">Cancel</button>
              <button onClick={() => { if (paidThroughDate && window.confirm(`Mark everything owed through ${fmtDay(paidThroughDate)} as paid?`)) markPaidThrough.mutate({ through_date: paidThroughDate, method: paidThroughMethod }); }}
                disabled={readOnly || (!paidThroughDate || markPaidThrough.isLoading)} className="btn-primary flex-1 disabled:opacity-50">
                {markPaidThrough.isLoading ? 'Marking…' : 'Mark paid'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isLoading && !data ? (
        <div className="text-center text-gray-500 py-10">Loading…</div>
      ) : people.length === 0 ? (
        <div className="card p-10 text-center text-gray-500">{range && !weeks.length ? 'No pay week starts in these dates — a pay week counts where its Monday falls. Try a span that holds a Monday.' : `Nobody has pay in ${range ? 'these dates' : `this ${windowNoun(viewMode)}`} yet.`}</div>
      ) : (
        <div className="card overflow-hidden">
          <ul className="divide-y divide-gray-100">
            {people.map(p => (
              <li key={p.id}>
                <button onClick={() => { setPerson(p.id); setStatus('all'); }} className="w-full text-left px-4 py-3.5 flex items-center gap-3 hover:bg-gray-50 active:bg-gray-100">
                  <div className="h-9 w-9 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-brand-700">{p.name.charAt(0)}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 truncate">{p.name}</p>
                    <p className="text-xs text-gray-500">
                      {p.rows.length} job{p.rows.length === 1 ? '' : 's'} · {p.hours.toFixed(1)} h{p.estRows ? ` (+${p.estRows} est.)` : ''}<TravelBit personId={p.id} from={travelFrom} to={travelTo} />
                      {p.sent + p.confirmed > 0 ? ` · paid ${money(p.sent + p.confirmed)}` : ''}
                    </p>
                    {p.tipChecks > 0 && <p className="text-[11px] text-amber-700 font-semibold">A tip to check — some of it went to someone not on the visit</p>}
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-black tabular-nums text-gray-900">{money(p.total)}</p>
                    <p className={`text-[10px] uppercase tracking-wide ${p.owed > 0 ? 'text-amber-700 font-semibold' : 'text-gray-500'}`}>{p.owed > 0 ? `${money(p.owed)} owed` : 'paid up'}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-gray-300 flex-shrink-0" />
                </button>
              </li>
            ))}
          </ul>
          <div className="px-4 py-2.5 bg-gray-50 border-t border-gray-200 text-xs text-gray-600 flex flex-wrap gap-x-4 gap-y-1 items-center">
            <span className="text-sm">Total pay <b className="text-gray-900">{money(periodTotal)}</b></span>
            <span>Owed <b className="text-amber-700">{money(totals.owed || 0)}</b></span>
            <span>Sent <b className="text-blue-700">{money(totals.sent || 0)}</b></span>
            <span>Confirmed <b className="text-green-700">{money(totals.confirmed || 0)}</b></span>
            <span>{entries.length} job{entries.length === 1 ? '' : 's'} · {periodHours.toFixed(1)} h clocked</span>
            <span className="ml-auto">{range ? `${weeks.length} pay week${weeks.length === 1 ? '' : 's'} · last due ${fmtDay(period.due)}` : `Due ${fmtDay(period.due)}`}</span>
          </div>
          <p className="px-4 py-2 border-t border-gray-100 text-[11px] text-gray-500">Owed is what the ledger still has to pay: stops at estimate + drive, tips, bonuses — minus what you marked sent. The crew's phone reads the same ledger.</p>
        </div>
      )}
    </div>
  );
}

/**
 * The driving between stops for the weeks on screen — worked out from when each
 * stop finished and the next began, logged breaks taken out.
 */
function TravelBit({ personId, from, to }: { personId: number; from: string; to: string }) {
  const { data } = useQuery(['travel', personId, from, to],
    () => api.get('/crew-day/travel', { params: { crew_member_id: personId, from, to } }).then(r => r.data),
    { staleTime: 5 * 60 * 1000 });
  if (!data || !(Number(data.total_minutes) > 0)) return null;
  return <span> · {data.total_minutes} min driving</span>;
}

// ── Audit log for one entry ──────────────────────────────────────────────────
function EntryHistory({ entryId }: { entryId: number }) {
  const { data, isLoading } = useQuery(['crew-pay-events', entryId], () => api.get(`/crew-pay/${entryId}/events`).then(r => r.data.events as any[]));
  const events = data || [];
  return (
    <div className="mt-2 ml-1 border-l-2 border-gray-200 pl-3 space-y-1">
      {isLoading ? <p className="text-xs text-gray-500">Loading history…</p>
        : events.length === 0 ? <p className="text-xs text-gray-500">No history yet.</p>
        : events.map((ev: any) => (
          <p key={ev.id} className="text-xs text-gray-600">
            <span className="text-gray-500">{fmtTs(ev.created_at, true)}</span> · <span className="font-semibold capitalize">{ev.action}</span>
            {ev.amount != null ? ` ${money(ev.amount)}` : ''}{ev.method ? ` via ${METHOD_LABEL[ev.method] || ev.method}` : ''}
            {ev.actor_name ? ` — ${ev.actor_name}` : ''}{ev.note ? ` · ${ev.note}` : ''}
          </p>
        ))}
    </div>
  );
}

// ── Mark sent sheet ──────────────────────────────────────────────────────────
function SentSheet({ entry, methods, lastMethod, method, setMethod, amount, setAmount, note, setNote, busy, onClose, onSave }: {
  entry: Entry; methods: string[]; lastMethod?: string;
  method: string; setMethod: (m: string) => void; amount: string; setAmount: (a: string) => void; note: string; setNote: (n: string) => void;
  busy: boolean; onClose: () => void; onSave: () => void;
}) {
  const readOnly = useReadOnly();   // view-only login: grey out what can only ever be refused
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-gray-900">Mark sent — {entry.employee_name}</h3>
          <button onClick={onClose}><X className="h-5 w-5 text-gray-500" /></button>
        </div>
        <p className="text-sm text-gray-600">{entry.customer_name}{entry.address ? ` · ${entry.address.split(',')[0]}` : ''}</p>
        <div>
          <label className="label-sm">How</label>
          <div className="grid grid-cols-5 gap-1.5">
            {methods.map(m => (
              <button key={m} type="button" onClick={() => setMethod(m)}
                className={`press py-2 rounded-xl text-xs font-bold border ${method === m ? 'bg-brand-600 text-black border-brand-600' : 'bg-white text-gray-700 border-gray-300'}`}>{METHOD_LABEL[m]}</button>
            ))}
          </div>
          {lastMethod && <p className="text-[11px] text-gray-500 mt-1">Last time: {METHOD_LABEL[lastMethod]}</p>}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label-sm">Amount</label>
            <input type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} className="input" />
          </div>
          <div>
            <label className="label-sm">Note / ref <span className="text-gray-500 font-normal">(optional)</span></label>
            <input value={note} onChange={e => setNote(e.target.value)} className="input" placeholder="Venmo memo, check #…" />
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={onClose} className="btn-secondary flex-1">Cancel</button>
          <button onClick={onSave} disabled={readOnly || (busy)} className="btn-primary flex-1 disabled:opacity-50">{busy ? 'Saving…' : 'Mark sent'}</button>
        </div>
      </div>
    </div>
  );
}
